import { useEffect, useRef, useState } from 'react';
import gridIcon from './img/grid.png';
import listIcon from './img/list.png';
import filtersIcon from './img/filters.png';
import { DEFAULT_DISTANCE_MI } from '../../utils/geo';

const DISTANCE_MIN_MI = 1;
const DISTANCE_MAX_MI = 20;

export default function AllRestaurantsControls({
  searchValue,
  onSearchChange,
  onSearchSubmit,
  appliedDistanceMi,
  onDistanceApply,
  onDistanceClear,
  hasCoords,
  sort,
  onSortChange,
  isRestaurantOfTheDay,
  onRestaurantOfTheDayChange,
  viewMode,
  onViewModeChange,
  onReset,
}) {
  const wrapRef = useRef(null);
  const [open, setOpen] = useState(false);
  const [sliderPreviewMi, setSliderPreviewMi] = useState(DEFAULT_DISTANCE_MI);

  useEffect(() => {
    if (appliedDistanceMi != null) {
      setSliderPreviewMi(appliedDistanceMi);
    } else {
      setSliderPreviewMi(DEFAULT_DISTANCE_MI);
    }
  }, [appliedDistanceMi]);

  useEffect(() => {
    if (!open) return undefined;
    const onPointerDown = (e) => {
      const root = wrapRef.current;
      if (!root || root.contains(e.target)) return;
      setOpen(false);
    };
    const onKeyDown = (e) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('pointerdown', onPointerDown, true);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('pointerdown', onPointerDown, true);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [open]);

  const sliderValue = sliderPreviewMi;
  const distanceDisabled = !hasCoords;
  const distanceDirty =
    hasCoords &&
    (appliedDistanceMi == null || sliderPreviewMi !== appliedDistanceMi);

  const fillRatio =
    (sliderValue - DISTANCE_MIN_MI) / (DISTANCE_MAX_MI - DISTANCE_MIN_MI || 1);

  const handleSliderChange = (e) => {
    const n = Number(e.target.value);
    if (!Number.isFinite(n)) return;
    setSliderPreviewMi(n);
  };

  const handleApplyDistance = () => {
    if (distanceDisabled) return;
    onDistanceApply(sliderPreviewMi);
    setOpen(false);
  };

  const handleSearchKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      onSearchSubmit?.();
    }
  };

  return (
    <div
      ref={wrapRef}
      className={`allrestaurants-controls-wrap${open ? ' is-open' : ''}`}
    >
      <div className="allrestaurants-controls">
        <div className="controls-left">
          <form
            className="search-wrap"
            onSubmit={(e) => {
              e.preventDefault();
              onSearchSubmit?.();
            }}
          >
            <span className="search-icon" aria-hidden="true">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M21 21L16.65 16.65M19 11C19 15.4183 15.4183 19 11 19C6.58172 19 3 15.4183 3 11C3 6.58172 6.58172 3 11 3C15.4183 3 19 6.58172 19 11Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </span>
            <input
              className="search-input"
              placeholder="Search for restaurant"
              aria-label="Search for restaurant"
              value={searchValue}
              onChange={(e) => onSearchChange(e.target.value)}
              onKeyDown={handleSearchKeyDown}
            />
            <button type="submit" className="search-submit-btn" aria-label="Search restaurants">
              Search
            </button>
          </form>

          <button
            className={`filters-btn${open ? ' active' : ''}`}
            type="button"
            onClick={() => setOpen((v) => !v)}
            aria-expanded={open}
            aria-controls="allrestaurants-filterbar"
          >
            <img src={filtersIcon} alt="" className="filters-icon" />
            <span>Filters</span>
          </button>
        </div>

        <div className="view-toggle" role="group" aria-label="Restaurant layout">
          <button
            className={`toggle-btn${viewMode === 'grid' ? ' active' : ''}`}
            type="button"
            onClick={() => onViewModeChange('grid')}
            aria-pressed={viewMode === 'grid'}
          >
            <img src={gridIcon} alt="" className="toggle-icon toggle-icon--grid" />
            <span>Grid</span>
          </button>
          <button
            className={`toggle-btn${viewMode === 'list' ? ' active' : ''}`}
            type="button"
            onClick={() => onViewModeChange('list')}
            aria-pressed={viewMode === 'list'}
          >
            <img src={listIcon} alt="" className="toggle-icon toggle-icon--list" />
            <span>List</span>
          </button>
        </div>
      </div>

      <div
        id="allrestaurants-filterbar"
        className={`allrestaurants-filterbar${open ? ' is-open' : ''}`}
        role="region"
        aria-label="Restaurant filters"
        hidden={!open}
      >
        <div className="filter-inline-group filter-inline-group--panel filter-inline-group--distance">
          <div className="filter-panel filter-panel--distance">
            <div className="filter-panel__head">
              <span className="filter-panel__title">Distance</span>
              {appliedDistanceMi != null && hasCoords ? (
                <div className="filter-panel__head-actions">
                  <span className="filter-panel__badge">Within {appliedDistanceMi} miles</span>
                  <button type="button" className="filter-panel__clear" onClick={onDistanceClear}>
                    Clear
                  </button>
                </div>
              ) : hasCoords ? (
                <span className="filter-panel__badge filter-panel__badge--muted filter-panel__badge--compact">
                  Showing all
                </span>
              ) : null}
            </div>
            <div className="filter-panel__slider-row">
              <span className="filter-panel__edge" aria-hidden>
                {DISTANCE_MIN_MI} mi
              </span>
              <div className="filter-panel__track-wrap">
                <input
                  type="range"
                  className={`filter-panel__range${appliedDistanceMi != null ? ' is-active' : ''}`}
                  min={DISTANCE_MIN_MI}
                  max={DISTANCE_MAX_MI}
                  step={1}
                  value={sliderValue}
                  onChange={handleSliderChange}
                  disabled={distanceDisabled}
                  style={{ '--range-fill': String(fillRatio) }}
                  aria-label={`Maximum distance: ${sliderValue} miles`}
                  aria-valuemin={DISTANCE_MIN_MI}
                  aria-valuemax={DISTANCE_MAX_MI}
                  aria-valuenow={sliderValue}
                  aria-valuetext={`${sliderValue} miles`}
                />
              </div>
              <span className="filter-panel__edge" aria-hidden>
                {DISTANCE_MAX_MI} mi
              </span>
            </div>
            {hasCoords ? (
              <div className="filter-panel__apply-row">
                {appliedDistanceMi == null ? (
                  <p className="filter-panel__apply-hint">
                    Apply to filter within {sliderValue} miles (default is {DEFAULT_DISTANCE_MI} mi)
                  </p>
                ) : null}
                <button
                  type="button"
                  className="filter-panel__apply-btn"
                  onClick={handleApplyDistance}
                  disabled={distanceDisabled || (!distanceDirty && appliedDistanceMi != null)}
                >
                  Apply
                </button>
              </div>
            ) : (
              <p className="filter-panel__hint" role="status">
                Set a delivery address to filter by distance.
              </p>
            )}
          </div>
        </div>

        <div className="filter-inline-group filter-inline-group--panel">
          <div className="filter-panel filter-panel--sort">
            <div className="filter-panel__head">
              <span className="filter-panel__title">Sort by distance</span>
            </div>
            <div className="filter-panel__body">
              <div className="segmented">
                <button
                  type="button"
                  className={`segment-btn${sort === 'asc' ? ' is-active' : ''}`}
                  onClick={() => onSortChange('asc')}
                  aria-pressed={sort === 'asc'}
                >
                  Ascending
                </button>
                <button
                  type="button"
                  className={`segment-btn${sort === 'desc' ? ' is-active' : ''}`}
                  onClick={() => onSortChange('desc')}
                  aria-pressed={sort === 'desc'}
                >
                  Descending
                </button>
              </div>
            </div>
          </div>
        </div>

        <div className="filter-inline-group filter-inline-group--panel">
          <div className="filter-panel filter-panel--featured">
            <div className="filter-panel__head">
              <span className="filter-panel__title">All Featured Restaurants</span>
            </div>
            <div className="filter-panel__body">
              <button
                type="button"
                className={`filter-chip filter-chip--featured${isRestaurantOfTheDay ? ' is-active' : ''}`}
                onClick={() => onRestaurantOfTheDayChange(!isRestaurantOfTheDay)}
                aria-pressed={isRestaurantOfTheDay}
              >
                Featured Restaurant
              </button>
            </div>
          </div>
        </div>

        <div className="filter-inline-group filter-inline-group--panel filter-inline-group--actions">
          <div className="filter-panel filter-panel--reset">
            <div className="filter-panel__actions-row">
              <button className="filters-reset-btn" type="button" onClick={onReset}>
                Reset Filters
              </button>
              <button
                className="filters-close-btn"
                type="button"
                onClick={() => setOpen(false)}
                aria-controls="allrestaurants-filterbar"
                aria-label="Close filters panel"
              >
                Close filters
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
